import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';

export const ContentSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  height: 100%;
`;

export const ActionButton = styled.button`
  width: 100%;
  height: 50px;
  margin-top: 24px;

  border: 1px solid #5e84ed;
  background: transparent;
  border-radius: 8px;

  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 50.84px;
  text-align: center;
  color: #5e84ed;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover:enabled {
    opacity: 0.85;
  }
  &:disabled {
    cursor: not-allowed;
  }
`;

export const CountdownLabel = styled.span`
  font-family: 'Inter', sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 12px;
  color: #6d6d6d;
  text-align: center;
  padding-top: 8px;
`;

export const StatusLabel = styled.span<{ status: 'error' | 'success' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 12px;
  padding-top: 8px;
  ${({ status }) =>
    status === 'error'
      ? `color:#D64545; background: transparent;`
      : `background:transparent; color:#5E84ED;`}
`;

export const PageBody = styled.div`
  width: 100%;
`;

export const Prefix = styled.div`
  display: flex;
  align-items: center;
  background: #2B2D33;
  border-radius: 8px;
  padding: 8px;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  pointer-events: auto;
  user-select: none;
  
  &:active {
    transform: scale(0.98);
  }
`;

export const Flag = styled.img`
  width: 32.02px;
  height: 16.80px;
  border-radius: 4px;
  pointer-events: none;
  user-select: none;
`;

export const PrefixText = styled.span`
  font-family: 'Inter', sans-serif;
  color: #E9ECF5;
  font-size: 16px;
  font-weight: 500;
  line-height: 19.20px;
  letter-spacing: 1px;
  word-wrap: break-word;
  pointer-events: none;
  user-select: none;
`;

export const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
  z-index: 1;
`;

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-8px);
  }
`;

export const DropdownMenu = styled.div<{ isOpen: boolean; isClosing: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  border-radius: 12px;
  padding: 12px 8px;
  z-index: 1000;
  overflow-y: auto;
  display: flex;
  pointer-events: auto;
  background: #1E1F22;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.20);
  border-radius: 12px;
  outline: 1px #2B2D33 solid;
  outline-offset: -1px;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  
  ${({ isOpen, isClosing }) => {
    if (!isOpen) return css`display: none; pointer-events: none;`;
    if (isClosing) {
      return css`
        animation: ${slideUp} 0.2s ease-out forwards;
        pointer-events: none;
      `;
    }
    return css`
      animation: ${slideDown} 0.2s ease-out forwards;
      pointer-events: auto;
    `;
  }}
`;

export const DropdownOption = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  pointer-events: auto;
  user-select: none;
  
  /* Selected state: darker background */
  background: ${({ isSelected }) => (isSelected ? '#202226' : 'transparent')};
  
  /* Hover: keep selected color when already selected */
  &:hover {
    background: ${({ isSelected }) => (isSelected ? '#202226' : '#33353B')};
  }
  
  /* Tap / click (active) color */
  &:active {
    transform: scale(0.98);
    background: #23252A;
  }
`;

export const OptionLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  pointer-events: none;
`;

export const OptionFlag = styled.img`
  width: 32.02px;
  height: 16.80px;
  border-radius: 4px;
  pointer-events: none;
  user-select: none;
`;

export const OptionName = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #E9ECF5;
  pointer-events: none;
  user-select: none;
  line-height: 19.20px;
`;

export const OptionCode = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #B7BDCB;
  pointer-events: none;
  user-select: none;
  line-height: 19.20px;
`;

export const CheckboxRow = styled.div`
  margin-top: 0;
  width: 100%;
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const checkboxPop = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.6);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

export const CheckboxInput = styled.input`
  width: 21px;
  height: 21px;
  margin-top: 2px;
  flex: 0 0 auto;

  /* FORCE TRANSPARENCY — REQUIRED */
  appearance: none;
  -webkit-appearance: none;
  background-color: transparent;
  background: transparent;

  border-radius: 4px;
  border: 1px solid #b7bdcb;
  opacity: 1;

  /* this only applies once manually styled */
  accent-color: #5e84ed;

  /* draw checkmark when checked */
  display: inline-flex;
  align-items: center;
  justify-content: center;

  /* subtle animated feel to match other components */
  transition:
    background-color 0.18s ease-out,
    border-color 0.18s ease-out,
    box-shadow 0.18s ease-out,
    transform 0.14s ease-out;

  &:active {
    transform: scale(0.94);
  }

  &:checked {
    background-color: #5e84ed;
    border-color: #5e84ed;
    transform: scale(1.05);
  }

  &:checked::after {
    content: '✔';
    color: white;
    font-size: 14px;
    line-height: 1;
    opacity: 0;
    transform: scale(0.6);
    animation: ${checkboxPop} 0.18s ease-out forwards;
  }
`;

export const CheckboxLabel = styled.label`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 13px;
  line-height: 140%;
  letter-spacing: 0px;
  vertical-align: middle;
  color: #b7bdcb;
  cursor: pointer;
`;

export const ActionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
`;

/* error under the SMS-consent row */
export const ConsentError = styled(StatusLabel)`
  margin-top: 8px;
`;

export const CodeGlowWrap = styled.div<{ status: 'idle' | 'error' | 'success' }>`
  width: 100%;

  /* target the individual boxes inside VerificationCodeInput */
  input {
    transition:
      box-shadow 0.18s ease-out,
      border-color 0.18s ease-out;
  }

  ${({ status }) =>
    status === 'success'
      ? `
        input {
          border-color: #5E84ED !important;
          box-shadow: 0 0 0 2px rgba(94, 132, 237, 0.28), 0 0 18px rgba(94, 132, 237, 0.22);
        }
      `
      : status === 'error'
        ? `
        input {
          border-color: #D64545 !important;
          box-shadow: 0 0 0 2px rgba(214, 69, 69, 0.28), 0 0 18px rgba(214, 69, 69, 0.22);
        }
      `
        : ``}
`;
